import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { BsGem } from 'react-icons/bs';
import Input from 'modules/common/Input';
import Button from 'modules/common/Button';
import Footer from 'modules/partial/Footer';
import { BiChevronLeft } from 'react-icons/bi';
import { usePartner } from 'modules/auth/hooks/usePartner';
import { useBuyWallet, useWallet } from '../hooks/useWallet';
import ErrorMessage from '../components/ErrorMessage';
import useWalletOptions from '../hooks/useWalletOptions';

const CurrentBalance = ({ balance }) => (
  <div className="bg-gray-300 text-gray-800 rounded-2xl p-3 flex items-center space-x-3">
    <div className="flex-shrink-0">
      <BsGem className="h-7 w-7" />
    </div>
    <div className="text-sm">Current Gems</div>
    <div className="flex-1 text-right font-bold">{balance}</div>
  </div>
);

CurrentBalance.propTypes = {
  balance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

const Label = ({ value }) => (
  <div className="text-lg font-bold py-4">{value}</div>
);

Label.propTypes = {
  value: PropTypes.string.isRequired,
};

const QuickPurchaseButton = ({
  label,
  subLabel,
  priceLabel,
  icon: Icon,
  isSelected,
  ...props
}) => (
  <Button
    className={cn(
      'w-full',
      isSelected ? 'ring-2 ring-primary-500 scale-100' : 'scale-95'
    )}
    {...props}
  >
    <div className="flex items-center justify-between w-full">
      <div className="h-10 w-10 rounded-full bg-primary-100 flex justify-center items-center">
        {typeof Icon === 'string' ? (
          <img
            className="h-9 w-9 object-cover object-center"
            src={Icon}
            alt={label}
          />
        ) : (
          <Icon className="h-6 w-6 text-primary-700" />
        )}
      </div>
      <div className="text-right">
        <div className="text-lg font-bold text-primary-600">{label}</div>
        <div className="flex divide-x text-xs">
          {subLabel ? <div className="font-light pr-3">{subLabel}</div> : null}
          <div className="font-light pl-3">{priceLabel}</div>
        </div>
      </div>
    </div>
  </Button>
);

QuickPurchaseButton.defaultProps = {
  subLabel: null,
  isSelected: false,
};

QuickPurchaseButton.propTypes = {
  isSelected: PropTypes.bool,
  label: PropTypes.string.isRequired,
  subLabel: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.string,
  ]),
  priceLabel: PropTypes.string.isRequired,
  icon: PropTypes.instanceOf(Object).isRequired,
};

const CUSTOM_AMOUNT = false;

function BuyGems() {
  const [partner] = usePartner();
  const [error, setError] = React.useState(null);
  const [data] = useWallet();
  const [isOptionsLoading, options] = useWalletOptions();
  const handleBack = () => {
    window.location.href = '/';
  };
  const [selected, setSelected] = React.useState('');
  const handleQuick = (newValue) => () => {
    setSelected(newValue);
  };
  const [isLoading, buy] = useBuyWallet();
  const [amount, setAmount] = React.useState('');
  const [purchase, setPurchase] = React.useState(null);
  const handleBuy = async () => {
    const res = await buy({
      amount: selected?.price,
      redirect_url: `https://games-lobby-stg.ayos.com/?partner_id=${partner.id}`,
    });
    if (res?.error) {
      setError(res?.data);
      return;
    }
    setPurchase(res);
  };
  return (
    <>
      <ErrorMessage error={error} setError={setError} />
      <BuyConfirm data={purchase} />
      <div className="flex items-center px-6 pt-6 pb-0">
        <div className="w-24 flex-shrink-0 flex items-center">
          <button type="button" onClick={handleBack}>
            <BiChevronLeft className="h-14 w-14" />
          </button>
        </div>
        <div className="flex-1">
          <CurrentBalance balance={data?.balance || '0'} />
        </div>
      </div>
      <div className="flex-1 px-6">
        {CUSTOM_AMOUNT ? (
          <div className="border-b pb-6">
            <Label value="Buy Games" />
            <div className="relative">
              <Input
                name="amount"
                label=""
                placeholder="Enter Amount"
                type="number"
                onChange={({ target }) => setAmount(target.value)}
                value={amount}
              />
              {amount ? (
                <div className="absolute top-0 right-0 z-10 py-2 px-4">
                  <button
                    className="text-primary-500 text-sm"
                    type="button"
                    onClick={() => setSelected('')}
                  >
                    Clear
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        <Label value="Select Package" />
        <div className="space-y-2 -mx-5">
          {options.length < 1 ? (
            <div className="text-center text-gray-500">
              {isOptionsLoading ? 'Loading...' : 'There are no options'}
            </div>
          ) : (
            options.map((item) => (
              <QuickPurchaseButton
                label={item.label}
                subLabel={item.sub_label}
                priceLabel={item.price_label}
                icon={item.icon || BsGem}
                onClick={handleQuick(item)}
                isSelected={item === selected}
              />
            ))
          )}
        </div>
      </div>
      <div className="px-6 mt-6">
        {selected ? (
          <Button
            className="w-full"
            primary
            onClick={handleBuy}
            disabled={isLoading}
          >
            BUY {selected?.price_label}
          </Button>
        ) : (
          <Button className="w-full" primary disabled>
            BUY GEMS
          </Button>
        )}
      </div>
      {!partner?.is_white_labeled ? (
        <div className="p-6">
          <Footer />
        </div>
      ) : (
        <div className="pb-6" />
      )}
    </>
  );
}

BuyGems.propTypes = {};

function BuyConfirm({ data }) {
  const handleDismiss = () => {
    window.open(data?.payment_url, '_self');
  };
  if (!data) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="m-auto relative z-10 bg-gray-50 w-full max-w-xs rounded-lg">
        <div className="py-5 space-y-3">
          {Array.isArray(data?.items) ? (
            <div className="divide-y">
              {/* <div className="px-5 pb-2 text-sm">Item(s)</div>
              {React.Children.toArray(
                data?.items.map((item) => (
                  <div className="px-5 py-2 flex space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-primary-100 flex justify-center items-center">
                        render icon
                      </div>
                    </div>
                    <div className="uppercase">{item.label}</div>
                  </div>
                ))
              )} */}
              <div className="px-5 pt-2 flex justify-between">
                <div>Amount to pay</div>
                <div className="font-semibold">{data.total_amount}</div>
              </div>
            </div>
          ) : null}
          <div className="flex justify-center">
            <Button primary onClick={handleDismiss}>
              Proceed to Payment
            </Button>
          </div>
        </div>
      </div>
      <div className="absolute h-full w-full bg-black bg-opacity-25 backdrop-blur-sm" />
    </div>
  );
}

BuyConfirm.defaultProps = {
  data: null,
};

BuyConfirm.propTypes = {
  data: PropTypes.instanceOf(Object),
};

export default BuyGems;
